// components/SheetTable.js
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import {
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
} from "@coreui/react";
import TextEditor from "./TextEditor";
import CIcon from "@coreui/icons-react";
import { cilPlus, cilTrash } from "@coreui/icons";

import { HotTable } from "@handsontable/react";
import Handsontable from "handsontable";
import "handsontable/dist/handsontable.full.min.css";
import { HyperFormula } from "hyperformula";
import moment from "moment";

const hyperformulaInstance = HyperFormula.buildEmpty({
  licenseKey: "internal-use-in-handsontable",
});

// Formatos aceites ao colar datas (ex.: vindas do Excel), além do dateFormat
// da própria coluna. A ordem importa: o primeiro formato que validar
// (em modo estrito) "ganha".
const PASTE_DATE_FORMATS = [
  "YYYY-MM-DD",
  "YYYY/MM/DD",
  "DD/MM/YYYY",
  "DD-MM-YYYY",
  "DD.MM.YYYY",
  "DD/MM/YY",
  "DD-MM-YY",
  "MM/DD/YYYY",
  "D/M/YYYY",
  "D-M-YYYY",
];

// Excel guarda datas como nº de dias desde 1899-12-30. Isto só se aplica
// quando a célula de origem não estava formatada como data e o valor colado
// chega como um número simples.
const EXCEL_EPOCH = "1899-12-30";
const EXCEL_SERIAL_RANGE = [20000, 60000]; // ~1954-2064

function normalizePastedDate(value, dateFormat) {
  if (value == null) return value;

  const raw = String(value).trim();
  if (raw === "") return value;

  // já está no formato esperado pela coluna
  if (moment(raw, dateFormat, true).isValid()) return raw;

  if (/^\d{4,6}$/.test(raw)) {
    const serial = Number(raw);
    if (serial > EXCEL_SERIAL_RANGE[0] && serial < EXCEL_SERIAL_RANGE[1]) {
      return moment(EXCEL_EPOCH).add(serial, "days").format(dateFormat);
    }
  }

  const parsed = moment(raw, PASTE_DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format(dateFormat) : value;
}

// ===== helpers =====
const __kv_norm = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

// Resolve de _kv_config (se existir) ou de source (inicial)
function resolveKeyValueSource(cellProperties, instance, row) {
  const raw = cellProperties?._kv_config ?? cellProperties?.source;

  // 1) array fixo: [{ _id, label }]
  if (Array.isArray(raw)) return raw;
  // 2) condicional: { prop, value }
  if (raw && typeof raw === "object" && raw.prop) {
    const depProp = raw.prop;
    const depVal = instance.getDataAtRowProp(row, depProp);
    const opts = raw.value?.[depVal];
    return Array.isArray(opts) ? opts : [];
  }

  // fallback sempre []
  return [];
}

// ===== EDITOR =====
class KeyValueAutocompleteEditor extends Handsontable.editors
  .AutocompleteEditor {
  prepare(row, col, prop, td, value, cellProperties) {
    super.prepare(row, col, prop, td, value, cellProperties);

    const instance = this.hot.getInstance();

    // GUARDA a config original só uma vez
    if (typeof cellProperties._kv_config === "undefined") {
      cellProperties._kv_config = cellProperties.source;
    }

    // resolve dinamicamente para esta célula
    this._src = resolveKeyValueSource(cellProperties, instance, row);

    const ids = this._src.map((o) => String(o._id));
    const labels = this._src.map((o) => String(o.label));
    const toLabel = (id) => labels[ids.indexOf(String(id))];

    // calcular valor atual (id ou label)
    const rawVal = value != null ? String(value) : "";
    const byIdIdx = ids.indexOf(rawVal);
    const byLabelIdx =
      byIdIdx === -1
        ? labels.findIndex((l) => __kv_norm(l) === __kv_norm(rawVal))
        : -1;

    // guardamos o "selecionado" atual (para focar na abertura)
    this._currentId =
      byIdIdx !== -1
        ? ids[byIdIdx]
        : byLabelIdx !== -1
        ? ids[byLabelIdx]
        : null;

    super.setValue(null);

    // configs do autocomplete
    this.cellProperties.strict = true; // ENTER seleciona da lista
    this.cellProperties.filteringCaseSensitive = false;
    this.cellProperties.visibleRows = 8;

    // Fonte dinâmica para UI (NÃO destrói _kv_config)
    this.cellProperties.source = (query, callback) => {
      // re-resolve (caso a dependência tenha mudado)
      this._src = resolveKeyValueSource(cellProperties, instance, row);
      const currentLabels = this._src.map((o) => String(o.label));
      const q = __kv_norm(query);

      let choices = currentLabels;

      if (q) {
        // filtro: primeira opção do filtro fica 1ª (e portanto focada)
        choices = currentLabels.filter((lbl) => __kv_norm(lbl).includes(q));
      } else if (this._currentId) {
        // sem query: traz a opção atual para o topo para ficar focada
        const currentLabel = toLabel(this._currentId);
        if (currentLabel) {
          const rest = currentLabels.filter((l) => l !== currentLabel);
          choices = [currentLabel, ...rest];
        }
      }

      // guarda últimas escolhas (para fallback no commit)
      cellProperties._kv_lastChoices = choices;

      callback(choices);
    };
  }

  beginEditing(initialValue, event) {
    // mantém o valor definido em prepare e abre o dropdown
    super.beginEditing(initialValue, event);
    // seleciona todo o texto para que ao escrever o label seja substituído
    if (this.TEXTAREA && typeof this.TEXTAREA.select === "function") {
      this.TEXTAREA.value = "";
    }
  }

  setValue(value) {
    const src = this._src || [];
    const asStr = value == null ? "" : String(value);
    let v = asStr;

    // id -> label se conhecido
    const byId = src.find((o) => String(o._id) === asStr);
    if (byId) {
      v = String(byId.label);
    } else {
      const hasLabel = src.some((o) => __kv_norm(o.label) === __kv_norm(asStr));
      if (!hasLabel) v = null;
    }
    super.setValue(v);
  }

  // devolve sempre _id (aceita id já válido ou label válida)
  getValue() {
    const val = (super.getValue() ?? "").toString().trim();
    const src = this._src || [];

    const byId = src.find((o) => String(o._id) === val);
    if (byId) return byId._id;

    const byLabel = src.find((o) => __kv_norm(o.label) === __kv_norm(val));
    if (byLabel) return byLabel._id;

    return null; // deixa o validator decidir
  }
}

// ===== VALIDATOR =====
const keyValueListValidator = function (value, callback) {
  const instance = this.instance;
  const row = this.row;
  const src = resolveKeyValueSource(this, instance, row);

  let v = value;
  if (v === null || v === void 0) v = "";
  v = String(v).trim();

  if ((this.allowEmpty && v === "") || src.length === 0) {
    callback(true);
    return;
  }

  const okById = src.some((o) => String(o._id) === v);
  const okByLabel = src.some((o) => __kv_norm(o.label) === __kv_norm(v));

  callback(okById || okByLabel);
};

// ===== RENDERER =====
const keyValueListRenderer = function (
  hot,
  TD,
  row,
  col,
  prop,
  value,
  cellProperties
) {
  const src = resolveKeyValueSource(cellProperties, hot, row);
  const v = value != null ? String(value) : "";

  let display = v;
  let item = src.find(({ _id }) => String(_id) === v);
  if (!item) item = src.find(({ label }) => __kv_norm(label) === __kv_norm(v));
  if (item) display = item.label;

  TD.title = display || "";

  Handsontable.renderers
    .getRenderer("autocomplete")
    .call(hot, hot, TD, row, col, prop, display, cellProperties);
};

// ===== REGISTO =====
Handsontable.cellTypes.registerCellType("select-key-value", {
  editor: KeyValueAutocompleteEditor,
  validator: keyValueListValidator,
  renderer: keyValueListRenderer,
});

// ===== LONGTEXT-MODAL =====
// Editor that fires a bubbling DOM event to open a React modal; never edits inline.
let _longtextModalRegistered = false;
if (!_longtextModalRegistered) {
  _longtextModalRegistered = true;

  class LongtextModalEditor extends Handsontable.editors.BaseEditor {
    prepare(row, col, prop, td, value, cellProperties) {
      super.prepare(row, col, prop, td, value, cellProperties);
      this._maxLength = cellProperties.maxLength ?? null;
      this._label = cellProperties.label ?? null;
    }

    open() {
      const evt = new CustomEvent("ht:longtext:open", {
        bubbles: true,
        detail: {
          row: this.row,
          col: this.col,
          prop: this.prop,
          value: this.originalValue ?? "",
          maxLength: this._maxLength,
          label: this._label,
        },
      });
      this.TD.dispatchEvent(evt);
      this.finishEditing(true); // restore original, close inline editor
    }

    close() {}
    focus() {}
    getValue() { return this.originalValue; }
    setValue(value) { this.originalValue = value; }
  }

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = String(html ?? "");
    return (div.textContent ?? "").replace(/\s+/g, " ").trim();
  };

  const longtextRenderer = function (hot, TD, row, col, prop, value, cellProperties) {
    const text = stripHtml(value);
    const MAX_PREVIEW = 80;

    TD.style.overflow = "hidden";
    TD.style.textOverflow = "ellipsis";
    TD.style.whiteSpace = "nowrap";
    TD.style.padding = "8px 12px";
    TD.title = text;

    if (!cellProperties.readOnly) TD.style.cursor = "pointer";

    if (text.length === 0) {
      TD.style.color = "#adb5bd";
      TD.style.fontStyle = "italic";
      TD.textContent = cellProperties.readOnly ? "" : "Clique para editar…";
    } else {
      TD.style.color = "";
      TD.style.fontStyle = "";
      TD.textContent = text.length > MAX_PREVIEW ? text.slice(0, MAX_PREVIEW) + "…" : text;
    }
  };

  Handsontable.cellTypes.registerCellType("longtext-modal", {
    editor: LongtextModalEditor,
    renderer: longtextRenderer,
  });
}

const SheetTable = forwardRef(
  (
    {
      nestedHeaders,
      columns,
      data,
      rowHeaders,
      cells,
      rowMove,
      useFormula,
      sheetName,
      filters,
      autoId,
      allowInvalid,
      setData,
      maxHeight,
      readOnly = false,
      columnHeaderHeight,
    },
    ref
  ) => {
    const isReadOnly = Boolean(readOnly);
    // A custom header height (a number, or one per nested header level)
    // means the titles may need several lines.
    const wrapHeaders = columnHeaderHeight != null;
    const headerHeights = Array.isArray(columnHeaderHeight)
      ? columnHeaderHeight
      : [columnHeaderHeight ?? 55];
    const headerHeight = headerHeights.reduce((acc, h) => acc + h, 0);

    // Two-level nested headers: columns with an empty bottom label are shown
    // as a single cell spanning both levels (rowspan-like).
    const rowspanColumns = useMemo(() => {
      if (!wrapHeaders || nestedHeaders?.length !== 2) return new Set();
      const bottom = nestedHeaders[1].flatMap((cell) => {
        const label = typeof cell === "object" ? cell.label : cell;
        return Array(cell?.colspan ?? 1).fill(label ?? "");
      });
      return new Set(
        bottom.flatMap((label, col) => (label === "" ? [col] : [])),
      );
    }, [wrapHeaders, nestedHeaders]);
    const [selectedRows, setSelectedRows] = useState(false);
    const hotRef = useRef(null);
    const noDataRef = useRef(null);
    const containerRef = useRef(null);

    const [backendErrors, setBackendErrors] = useState([]);
    const [invalidCells, setInvalidCells] = useState([]);
    const [longtextModal, setLongtextModal] = useState(null);

    useImperativeHandle(ref, () => ({
      applyBackendErrors: (errors) => {
        const hot = hotRef.current?.hotInstance;
        if (!hot) return;

        const newBackendErrors = errors
          .flatMap((error, row) => {
            return Object.entries(error)
              .map(([colKey, message]) => {
                const col = hot.propToCol(colKey);

                if (col == null || col < 0) return;

                const msg = Array.isArray(message)
                  ? message.join(", ")
                  : message || "";

                return {
                  row,
                  col,
                  comment: { value: msg, readOnly: true },
                  valid: false,
                };
              })
              .filter(Boolean);
          })
          .filter(Boolean);
        setBackendErrors(newBackendErrors);
      },
    }));

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target) &&
          !event.target.closest("[data-hot-input]") &&
          !event.target.closest(".htDatepickerHolder")
        ) {
          const hot = hotRef.current.hotInstance;
          if (hot.getSelected()) {
            hot.deselectCell();
            setSelectedRows(false);
          }
        }
      };

      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }, []);

    useEffect(() => {
      const handleOpen = (e) => {
        setLongtextModal({
          row: e.detail.row,
          col: e.detail.col,
          prop: e.detail.prop,
          value: e.detail.value ?? "",
          maxLength: e.detail.maxLength ?? null,
          label: e.detail.label ?? null,
        });
      };
      const container = containerRef.current;
      container?.addEventListener("ht:longtext:open", handleOpen);
      return () => container?.removeEventListener("ht:longtext:open", handleOpen);
    }, []);

    const addRow = () => {
      if (isReadOnly) return;
      const hot = hotRef.current.hotInstance;
      if (!hot) return;

      noDataRef.current.style.display = "none";
      hot.alter("insert_row_below");
    };

    const deleteRows = (e) => {
      if (isReadOnly) return;
      e.preventDefault();

      const hot = hotRef.current.hotInstance;
      if (!hot) return;

      const selection = mergeRowSelections(hot.getSelected());
      const newData = [...data];
      let deleted = 0;
      if (selection) {
        // We process each selection range. Here, we'll simply remove rows from the first selection range.
        selection.forEach((range) => {
          const [startRow, , endRow] = range;

          const startRowOffset = startRow - deleted;
          const endRowOffset = endRow - deleted;
          // Remove rows starting from the bottom to avoid index issues.
          for (let row = endRowOffset; row >= startRowOffset; row--) {
            hot.alter("remove_row", row);
            newData.splice(row, 1);
            deleted += 1;
          }
        });

        hot.deselectCell();
        setData(newData);
      }
    };

    const handleSelection = (
      row,
      column,
      row2,
      column2,
      preventScrolling,
      selectionLayerLevel
    ) => {
      const hot = hotRef.current.hotInstance;
      if (column === -1) setSelectedRows(true);
      else setSelectedRows(false);
    };

    const handleDeselection = (
      row,
      column,
      row2,
      column2,
      preventScrolling,
      selectionLayerLevel
    ) => {
      setSelectedRows(false);
    };

    const handleChange = (changes) => {
      if (isReadOnly) return;
      if (!changes) return;

      setData((prevData) => {
        const newData = [...prevData];
        changes.forEach(([row, col, oldValue, newValue]) => {
          row = hotRef.current.hotInstance.toPhysicalRow(row);
          newData[row][col] = newValue;
        });
        return newData;
      });
    };

    const handleRowMove = (movedRows, finalIndex) => {
      if (isReadOnly) return;
      setData((prevData) => {
        const newData = [...prevData];
        const movedData = movedRows.map((rowIndex) => newData[rowIndex]);

        // Remove moved rows from original position
        movedRows
          .sort((a, b) => b - a)
          .forEach((rowIndex) => newData.splice(rowIndex, 1));

        // Insert rows at the new position
        newData.splice(finalIndex, 0, ...movedData);
        return newData;
      });
      hotRef.current?.hotInstance.loadData(data);
    };

    const afterCreateRow = (index, amount) => {
      if (isReadOnly) return;
      const hot = hotRef.current.hotInstance;
      const allowedKeys = columns.map((col) => col.data);
      let id = autoId
        ? data.reduce((acc, row) => (row.id > acc ? row.id : acc), 0) +
          data.filter((row) => !row.id).length
        : 0;

      let rowData = hot.getSourceData()[index];

      if (rowData) {
        for (let key in rowData) {
          if (!allowedKeys.includes(key)) {
            delete rowData[key];
          }
        }
      }

      if (autoId) {
        rowData = { id, ...rowData };
      }

      setData((prevData) => {
        const newData = [...prevData];
        newData[index] = rowData;
        return newData;
      });
    };

    const hotHeight = useMemo(() => {
      const currentLength = data.length * 50 + headerHeight;
      if (!maxHeight || currentLength < maxHeight) return "auto";
      return `${maxHeight}px`;
    }, [data, headerHeight]);

    const mapLabelSources = useMemo(() => {
      return (
        columns?.reduce((acc, c) => {
          if (c.data && Array.isArray(c.source)) {
            acc[c.data] = new Map(
              c.source.map((o) => [String(o.label).toLowerCase().trim(), o._id])
            );
          }
          return acc;
        }, {}) ?? {}
      );
    }, [columns]);

    const mapIdSources = useMemo(() => {
      if (!Array.isArray(columns)) return [];

      return columns.map((c) => {
        const m = new Map();
        const src = c?.source;

        // 1) fonte fixa: [{ _id, label }]
        if (Array.isArray(src)) {
          src.forEach((o) => m.set(String(o._id), o.label));
          return m;
        }

        // 2) condicional: { prop, value: { [depVal]: [{_id,label}] } }
        if (src && typeof src === "object" && src.value) {
          const mbv = src.value;
          // agrega TUDO, independentemente do valor dependente
          Object.values(mbv).forEach((arr) => {
            if (Array.isArray(arr)) {
              arr.forEach((o) => m.set(String(o._id), o.label));
            }
          });
          return m;
        }

        return m;
      });
    }, [columns]);

    const dateColumnsByProp = useMemo(() => {
      const map = {};
      (columns || []).forEach((c) => {
        if (c?.data && c.type === "date") {
          map[c.data] = c.dateFormat || "YYYY-MM-DD";
        }
      });
      return map;
    }, [columns]);

    const beforeChange = useCallback(
      (changes) => {
        if (!changes) return;
        changes.forEach((ch) => {
          const prop = ch[1];
          const newVal = ch[3];

          if (newVal == null) return;

          const dateFormat = dateColumnsByProp[prop];
          if (dateFormat) {
            // Aceita datas coladas (ex.: do Excel) em vários formatos e
            // converte sempre para o formato da coluna antes da validação.
            ch[3] = normalizePastedDate(newVal, dateFormat);
            return;
          }

          const key = String(newVal).toLowerCase().trim();
          const labelToId = mapLabelSources[prop];

          if (labelToId && labelToId.has(key)) {
            ch[3] = labelToId.get(key); // converte label -> id
          }
        });
      },
      [dateColumnsByProp, mapLabelSources]
    );

    const beforeCopy = useCallback(
      (data, coords) => {
        const colOffset = coords?.[0]?.startCol ?? 0;

        for (let r = 0; r < data.length; r++) {
          for (let c = 0; c < data[r].length; c++) {
            const v = data[r][c];
            const idToLabel = mapIdSources[c + colOffset];
            const mapped = idToLabel.get(String(v));
            if (mapped != null) data[r][c] = mapped; // id -> label
          }
        }
      },
      [hotRef, mapIdSources]
    );

    const handleAfterValidate = (isValid, value, row, prop /*, source */) => {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      const physRow = hot.toPhysicalRow(row);
      const col = hot.propToCol(prop);

      if (physRow == null || physRow < 0 || col == null || col < 0) {
        // Ignora metas para células que ainda não existem ou já foram removidas
        return;
      }

      setInvalidCells((prev) => {
        const key = (r, c) => `${r}:${c}`;
        const next = new Map(prev.map((e) => [key(e.row, e.col), e]));
        if (!isValid) {
          next.set(key(physRow, col), { row: physRow, col, valid: false });
        } else {
          next.delete(key(physRow, col));
        }
        return Array.from(next.values());
      });
    };

    const dataSchema = useMemo(() => {
      const schema = {};
      for (const c of columns || []) {
        if (c?.data) schema[c.data] = null;
      }
      if (autoId) schema.id = null;
      return schema;
    }, [columns, autoId]);

    return (
      <div ref={containerRef}>
        <HotTable
          ref={hotRef}
          dataSchema={dataSchema}
          data={data}
          colHeaders={columns.map((col) => col.title)}
          nestedHeaders={nestedHeaders}
          columns={columns}
          rowHeaders={rowHeaders}
          maxCols={columns.length}
          width="100%"
          height={hotHeight}
          stretchH="all"
          rowHeights={50}
          manualRowMove={isReadOnly ? false : rowMove}
          columnHeaderHeight={
            Array.isArray(columnHeaderHeight) ? columnHeaderHeight : headerHeight
          }
          colWidths={columns.map((col) => col.width)}
          readOnly={isReadOnly}
          formulas={
            useFormula
              ? {
                  engine: hyperformulaInstance,
                  sheetName,
                }
              : false
          }
          afterGetColHeader={(col, TH, level) => {
            if (filters && !columns[col]?.allowFiltering) {
              const button = TH.querySelector(".changeType");
              button?.parentElement.removeChild(button);
            }

            TH.style.textAlign = "center";
            TH.style.verticalAlign = "middle";
            TH.style.fontWeight = "bold";
            TH.style.fontFamily = "var(--cui-body-font-family)";
            TH.style.fontSize = "var(--cui-body-font-size)";

            if (wrapHeaders) {
              const label = TH.querySelector(".colHeader");
              if (label) {
                label.style.whiteSpace = "normal";
                label.style.lineHeight = "1.3";
              }
            }

            // Header cells are reused while scrolling: always reset first.
            const wrapper = TH.firstElementChild;
            const isRowspan =
              level === 0 && (col < 0 || rowspanColumns.has(col));
            if (rowspanColumns.size && wrapper) {
              TH.style.overflow = isRowspan ? "visible" : "";
              TH.style.position = isRowspan ? "relative" : "";
              Object.assign(wrapper.style, {
                position: isRowspan ? "absolute" : "",
                top: isRowspan ? "0" : "",
                left: isRowspan ? "0" : "",
                width: isRowspan ? "100%" : "",
                boxSizing: isRowspan ? "border-box" : "",
                height: isRowspan ? `${headerHeight - 1}px` : "",
                display: isRowspan ? "flex" : "",
                alignItems: isRowspan ? "center" : "",
                justifyContent: isRowspan ? "center" : "",
                background: isRowspan ? "inherit" : "",
                zIndex: isRowspan ? "1" : "",
              });
            }
          }}
          filters={filters}
          dropdownMenu={
            filters ? ["filter_by_value", "filter_action_bar"] : null
          }
          cell={[...backendErrors, ...invalidCells]}
          cells={cells}
          beforeChange={beforeChange}
          beforeCopy={beforeCopy}
          afterSelection={handleSelection}
          afterDeselect={handleDeselection}
          afterChange={handleChange}
          afterRowMove={handleRowMove}
          afterCreateRow={afterCreateRow}
          afterValidate={handleAfterValidate}
          allowInvalid={allowInvalid}
          outsideClickDeselects={false}
          comments={true}
          licenseKey="non-commercial-and-evaluation"
        />

        <div
          ref={noDataRef}
          style={{
            width: "100%",
            height: "55px",
            padding: "15px",
            borderLeft: "1px solid #ccc",
            borderBottom: "1px solid #ccc",
            borderRight: "1px solid #ccc",
            marginTop: "-1px",
            color: "#6c757d",
            background: "#f8f9fa",
            textAlign: "center",
            fontSize: "16px",
            display: data.length > 0 ? "none" : "block",
          }}
        >
          Nenhuma linha adicionada.
        </div>

        {!isReadOnly && (
          <div className="d-flex justify-content-end mt-2 gap-2">
            {selectedRows && (
              <>
                <CButton color="danger" size="sm" onClick={deleteRows}>
                  <CIcon icon={cilTrash} /> Remover
                </CButton>
              </>
            )}
            <CButton color="primary" size="sm" onClick={addRow}>
              <CIcon icon={cilPlus} /> Adicionar
            </CButton>
          </div>
        )}
        {longtextModal && (
          <CModal
            visible
            size="lg"
            onClose={() => setLongtextModal(null)}
            alignment="center"
          >
            <CModalHeader>
              <CModalTitle>{longtextModal.label ?? "Editar texto"}</CModalTitle>
            </CModalHeader>
            <CModalBody>
              <TextEditor
                value={longtextModal.value}
                maxLength={longtextModal.maxLength ?? undefined}
                onChange={(content) =>
                  setLongtextModal((prev) => ({ ...prev, value: content }))
                }
              />
            </CModalBody>
            <CModalFooter>
              <CButton color="secondary" onClick={() => setLongtextModal(null)}>
                Cancelar
              </CButton>
              <CButton
                color="primary"
                onClick={() => {
                  hotRef.current?.hotInstance?.setDataAtRowProp(
                    longtextModal.row,
                    longtextModal.prop,
                    longtextModal.value,
                  );
                  setLongtextModal(null);
                }}
              >
                Guardar
              </CButton>
            </CModalFooter>
          </CModal>
        )}

        <style>
          {`
            div.handsontable[id] {
                overflow: visible !important;
            }
                    
            /* === Container do dropdown (listbox) === */
            .handsontableEditor.autocompleteEditor.listbox {
              /* ganhar aos inline styles do HOT */
              height: auto !important;
              width: auto !important;
              overflow: visible !important;

              /* largura: legível em colunas pequenas, sem sair do ecrã */
              min-width: 240px !important;
              max-width: min(50vw, 480px) !important;

              /* aparência neutra alinhada com o tema base */
              background: #fff;
              border: 1px solid #dfe3e8;
              border-radius: 8px;
              box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
              z-index: 10000;
            }

            /* === Área “holder” (controla a ALTURA total visível) === */
            .handsontableEditor.autocompleteEditor.listbox .wtHolder {
              height: auto !important;                             /* override inline */
              max-height: clamp(180px, 30vh, 320px) !important;    /* alto q.b. */
              overflow-y: auto !important;
              overflow-x: hidden !important;
              overscroll-behavior: contain;
            }

            /* === Corrige tamanhos herdados pelos wrappers internos === */
            .handsontableEditor.autocompleteEditor.listbox .wtHider,
            .handsontableEditor.autocompleteEditor.listbox .wtSpreader {
              width: auto !important;
              height: auto !important;
            }

            /* === Tabela de opções: 1 linha com ellipsis === */
            .handsontableEditor.autocompleteEditor.listbox .htCore {
              table-layout: fixed;
              width: 100% !important;
            }
            .handsontableEditor.autocompleteEditor.listbox .htCore td,
            .handsontableEditor.autocompleteEditor.listbox .htCore th {
              padding: 8px 12px;
              height: 28px;
              line-height: 28px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              border: none;
            }

            .handsontableEditor.autocompleteEditor.listbox .current {
              background: #eee !important;
            }

            /* zebra suave coerente com o tema HOT */
            .handsontableEditor.autocompleteEditor.listbox .ht__row_even td {
              background: #fafbfc;
            }


            /* === elimina “buracos” quando abre por cima da célula === */
            .autocompleteEditor,
            .handsontableInputHolder .autocompleteEditor {
              min-height: 0 !important;
              height: auto !important;
            }

            /* clones que o HOT cria e não são usados na lista */
            .handsontableEditor.autocompleteEditor.listbox .ht_clone_top,
            .handsontableEditor.autocompleteEditor.listbox .ht_clone_bottom,
            .handsontableEditor.autocompleteEditor.listbox .ht_clone_left {
              display: none !important;
            }


            .handsontableEditor.autocompleteEditor.listbox .htCore tr.current td,
            .handsontableEditor.autocompleteEditor.listbox .htCore tr:hover td {
              background: #e7f1ff !important;
              font-weight: 600;
            }

            .handsontableEditor.autocompleteEditor.listbox .htCore tr.current td:first-child,
            .handsontableEditor.autocompleteEditor.listbox .htCore tr:hover td:first-child {
              position: relative;
            }
            .handsontableEditor.autocompleteEditor.listbox
              .htCore tr.current td:first-child::before,
            .handsontableEditor.autocompleteEditor.listbox
              .htCore tr:hover td:first-child::before {
              content: "";
              position: absolute; inset: 0 auto 0 0;
              width: 3px; background: #4b89ff;
              border-top-left-radius: 8px; border-bottom-left-radius: 8px;
            }
        `}
        </style>
      </div>
    );
  }
);

export default SheetTable;

function mergeRowSelections(selected) {
  // 1) Filtra seleções de linhas inteiras e normaliza [startRow, endRow]
  const ranges = (selected || [])
    .filter((a) => a[1] === -1) // só linhas inteiras
    .map(([r1, _c1, r2, _c2]) => {
      const start = Math.min(r1, r2);
      const end = Math.max(r1, r2);
      return [start, end];
    })
    .sort((a, b) => a[0] - b[0]); // ordena pelo start

  // 2) Faz merge de intervalos que se sobrepõem ou tocam (ex: 1-5 e 6-8 -> 1-8)
  const merged = [];
  for (const [s, e] of ranges) {
    if (!merged.length || s > merged[merged.length - 1][1] + 1) {
      merged.push([s, e]);
    } else {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    }
  }

  // 3) Reconstroi no formato de seleção do HOT mantendo col = -1
  //    e garantindo [rowMenor, -1, rowMaior, -1]
  return merged.map(([s, e]) => [s, -1, e, -1]);
}
